import React from "react";
import { Layout } from "../../components/layout/Layout";
import AutoScrollCarousel from "./AutoScrollCarousel";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { getHomeImageUrl, getServicesImageUrl } from "../../config/gcs";

export const images = [
  {
    src: getHomeImageUrl("bg-clip3.png"),
  },
  {
    src: getHomeImageUrl("bg-clip1.png"),
  },
  {
    src: getHomeImageUrl("bg-clip2.png"),
  },
  {
    src: getServicesImageUrl("project/mainbanner.webp"),
  },
];
function MainBenner() {
  const bannerRef = useRef(null); // Ref for the banner background
  const headingsRef = useRef([]); // Array of refs for each heading word
  const textRef = useRef(null); // Ref for the description text
  const carouselRef = useRef(null);

  useEffect(() => {
    if (bannerRef.current) {
      // Set initial state immediately to prevent flickering
      gsap.set(bannerRef.current, {
        x: "-100%",
        backgroundColor: "#0a1a2f",
        scale: 1.1,
        opacity: 0,
      });

      // Create a master timeline for smooth orchestration
      const masterTl = gsap.timeline({
        defaults: { ease: "power3.out" },
        delay: 0.1, // Small delay to ensure initial state is set
      });

      // Step 1: Smooth banner entrance with parallax effect
      masterTl.to(bannerRef.current, {
        x: "0%",
        backgroundColor: "#0a1a2f",
        scale: 1,
        opacity: 1,
        duration: 2.5,
        ease: "power3.out",
      });

      // Step 2: Prepare headings with smooth initial state
      gsap.set(headingsRef.current, {
        opacity: 0,
        y: 80,
        rotationX: 45,
        transformOrigin: "50% 50% -50px",
      });

      // Step 3: Create smooth staggered heading animations
      masterTl.add(() => {
        // Animate headings with smooth 3D rotation effect
        headingsRef.current.forEach((heading, index) => {
          if (heading) {
            gsap.to(heading, {
              opacity: 1,
              y: 0,
              rotationX: 0,
              duration: 1.2,
              ease: "back.out(1.7)",
              delay: index * 0.3, // Smooth staggered timing
            });
          }
        });
      }, "-=1.5"); // Start before banner animation completes

      // Step 4: Smooth text animation with typewriter effect
      masterTl.add(() => {
        if (textRef.current) {
          gsap.fromTo(
            textRef.current,
            {
              opacity: 0,
              y: 30,
              scale: 0.95,
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 1.5,
              ease: "power3.out",
            }
          );
        }
      }, "-=0.8");

      // Step 5: Smooth carousel entrance with parallax
      masterTl.add(() => {
        if (carouselRef.current) {
          gsap.fromTo(
            carouselRef.current,
            {
              opacity: 0,
              y: 50,
              scale: 0.9,
              rotationY: 15,
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              rotationY: 0,
              duration: 1.8,
              ease: "power3.out",
            }
          );
        }
      }, "-=0.5");

      // Add continuous subtle animations for engagement (reduced intensity to prevent flickering)
      masterTl.add(() => {
        // Subtle floating animation for headings (reduced movement)
        headingsRef.current.forEach((heading, index) => {
          if (heading) {
            gsap.to(heading, {
              y: "+=2", // Reduced from 5 to 2
              duration: 3 + index * 0.5, // Slower animation
              ease: "power1.inOut",
              yoyo: true,
              repeat: -1,
              delay: index * 0.3,
            });
          }
        });

        // Subtle scale animation for carousel (reduced scale)
        if (carouselRef.current) {
          gsap.to(carouselRef.current, {
            scale: 1.01, // Reduced from 1.02 to 1.01
            duration: 4, // Slower animation
            ease: "power1.inOut",
            yoyo: true,
            repeat: -1,
          });
        }
      }, "+=2"); // Increased delay before starting continuous animations
    }
  }, []);

  return (
    <div className="bg-bgWhite">
      <Layout>
        <div
          ref={bannerRef}
          className="flex text-left items-center relative md:mt-16 lg:mt-6 bg-[#0a1a2f] w-full h-[70vh] sm:h-[75vh] md:h-[85vh] rounded-[50px] opacity-0"
          style={{
            transform: "translateX(-100%) scale(1.1)",
            willChange: "transform, opacity",
          }}
        >
          <div className="flex flex-col my-auto w-full h-full justify-center items-center">
            <div className="flex -mt-5 sm:mt-0 items-start flex-col text-left self-start justify-start heading">
              <div
                className="ml-8 lg:pl-5 text-5xl sm:text-6xl md:text-7xl lg:text-8xl 2xl:text-9xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: `url('${images[0].src}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  animation: "moveHorizontal 3s infinite linear", // Apply the animation
                }}
                ref={(el) => (headingsRef.current[0] = el)}
              >
                EXHIBITION
              </div>

              <div
                className="ml-8 pt-4 sm:pt-8 lg:pl-5 lg:pt-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl 2xl:text-9xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: `url('${images[1].src}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  animation: "moveHorizontal 3s infinite linear", // Apply the animation
                }}
                ref={(el) => (headingsRef.current[1] = el)}
              >
                LOGISTICS &
              </div>

              <div className="ml-8 gap-4 pt-4 sm:pt-8 lg:pl-5 lg:pt-8 flex flex-col md:flex-row sm:gap-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl 2xl:text-9xl font-bold">
                {/* FREIGHT */}
                <div
                  className="text-transparent bg-clip-text"
                  style={{
                    backgroundImage: `url('${images[2].src}')`, // Separate image for FREIGHT
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    animation: "moveHorizontal 3s infinite linear", // Apply animation
                  }}
                  ref={(el) => (headingsRef.current[2] = el)}
                >
                  FREIGHT
                </div>

                {/* EXPERTS */}
                <div
                  className="text-transparent bg-clip-text"
                  style={{
                    backgroundImage: `url('${images[3].src}')`, // Separate image for EXPERTS
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    animation: "moveHorizontal 3s infinite linear", // Apply animation
                  }}
                  ref={(el) => (headingsRef.current[3] = el)}
                >
                  EXPERTS
                </div>
              </div>
            </div>
            {/* Positioned absolute to bottom right */}
            <div className="mb-4 sm:mb-4 ml-8 sm:w-[40vw] lg:mr-12 lg:mb-8 2xl:mb-16 lg:w-[40vw] 2xl:w-[20vw] absolute bottom-0 right-0   mr-4 flex flex-col justify-end items-end">
              <p ref={textRef} className="self-end text-gray-400 opacity-0">
                We specialize in delivering top-tier logistics solutions
                tailored to the unique demands of the exhibition industry.
              </p>
              {/*<i
                ref={arrowRef}
                className="text-gray-400 hidden  md:block sm:text-7xl  w-[23vw] mb-5 lg:w-[16vw] 2xl:w-[12vw] xl:mb-12 mt-5 cursor-pointer hover:text-gray-200 opacity-0"
              >
                <div className="">
                  <BsArrowDownCircle />
                </div>
              </i>*/}
            </div>
          </div>
        </div>
        <div
          ref={carouselRef}
          className="opacity-0 transform translate-y-10" // Start hidden and slightly below
        >
          <AutoScrollCarousel />
        </div>
      </Layout>
    </div>
  );
}

export default MainBenner;
